/**
 * ParentEditForm - Edit parent information with email change warning
 */

import React, { useState, useEffect } from 'react';
import { Grid, Col, FormInput, FormSelect, FormActionGroup } from './index';
import { AlertTriangle } from 'lucide-react';

const ParentEditForm = ({ parent, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    relationship_to_player: '',
    address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');

  useEffect(() => {
    if (parent) {
      // Normalize relationship value to lowercase for matching with select options
      const relationshipValue = parent.relationship_to_player
        ? parent.relationship_to_player.toLowerCase()
        : '';

      setFormData({
        first_name: parent.first_name || '',
        last_name: parent.last_name || '',
        email: parent.email || '',
        phone: parent.phone || '',
        relationship_to_player: relationshipValue,
        address: {
          address_line1: parent.address?.address_line1 || '',
          address_line2: parent.address?.address_line2 || '',
          city: parent.address?.city || '',
          state: parent.address?.state || '',
          zip_code: parent.address?.zip_code || ''
        }
      });
      setOriginalEmail(parent.email || '');
    }
  }, [parent]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Check if email is changing
      if (name === 'email' && value !== originalEmail) {
        setShowEmailWarning(true);
      } else if (name === 'email' && value === originalEmail) {
        setShowEmailWarning(false);
      }
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.address.address_line1.trim()) {
      newErrors['address.address_line1'] = 'Address is required';
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.state.trim()) {
      newErrors['address.state'] = 'State is required';
    }

    if (!formData.address.zip_code.trim()) {
      newErrors['address.zip_code'] = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      {showEmailWarning && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          padding: '1rem',
          backgroundColor: 'var(--color-warning-light)',
          border: '1px solid var(--color-warning)',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          color: 'var(--color-warning-dark)'
        }}>
          <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '0.2rem' }} />
          <div>
            <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Important: Email Change Notice</strong>
            <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.4' }}>
              Changing your email will update your username for logging in.
              You will receive confirmation emails at both your old and new email addresses.
            </p>
          </div>
        </div>
      )}

      <Grid columns={2} gap="1.25rem" style={{ marginBottom: '1.5rem' }}>
        <Col>
          <FormInput
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            error={errors.first_name}
          />
        </Col>

        <Col>
          <FormInput
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            error={errors.last_name}
          />
        </Col>

        <Col>
          <FormInput
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            error={errors.email}
          />
        </Col>

        <Col>
          <FormInput
            label="Phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            error={errors.phone}
          />
        </Col>

        <Col>
          <FormSelect
            label="Relationship to Player"
            name="relationship_to_player"
            value={formData.relationship_to_player}
            onChange={handleChange}
          >
            <option value="">Select...</option>
            <option value="mother">Mother</option>
            <option value="father">Father</option>
            <option value="guardian">Guardian</option>
            <option value="other">Other</option>
          </FormSelect>
        </Col>
      </Grid>

      <div style={{
        marginBottom: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid var(--color-border, #e0e0e0)'
      }}>
        <h4 style={{
          marginTop: 0,
          marginBottom: '1rem',
          color: 'var(--color-primary)',
          fontSize: '1.1rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>Address Information</h4>

        <Grid columns={1} gap="1.25rem" style={{ marginBottom: '1.25rem' }}>
          <Col>
            <FormInput
              label="Address Line 1"
              name="address.address_line1"
              value={formData.address.address_line1}
              onChange={handleChange}
              required
              error={errors['address.address_line1']}
            />
          </Col>

          <Col>
            <FormInput
              label="Address Line 2"
              name="address.address_line2"
              value={formData.address.address_line2}
              onChange={handleChange}
            />
          </Col>
        </Grid>

        <Grid columns={3} gap="1.25rem">
          <Col>
            <FormInput
              label="City"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              required
              error={errors['address.city']}
            />
          </Col>

          <Col>
            <FormInput
              label="State"
              name="address.state"
              value={formData.address.state}
              onChange={handleChange}
              maxLength={2}
              required
              error={errors['address.state']}
            />
          </Col>

          <Col>
            <FormInput
              label="ZIP Code"
              name="address.zip_code"
              value={formData.address.zip_code}
              onChange={handleChange}
              required
              error={errors['address.zip_code']}
            />
          </Col>
        </Grid>
      </div>

      <FormActionGroup
        type="submit"
        onCancel={onCancel}
        saveLabel="Save Changes"
        cancelLabel="Cancel"
      />
    </form>
  );
};

export default ParentEditForm;
